import { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Checkbox, FormControlLabel, InputAdornment, TextField, Typography } from '@mui/material';
import { apiRequest } from '../../services/apiService';

export interface ExpenseReimbursementValue {
  reimburseTechnician: boolean;
  reimbursementContractorId: string;
  reimbursementAmount: number | string;
}
export const emptyExpenseReimbursement: ExpenseReimbursementValue = {
  reimburseTechnician: false, reimbursementContractorId: '', reimbursementAmount: '',
};
export function ExpenseReimbursementFields({ eventId, value, onChange, disabled = false, clientAmount, error }: {
  eventId: string; value: ExpenseReimbursementValue; onChange: (next: ExpenseReimbursementValue) => void;
  disabled?: boolean; clientAmount: number | string; error?: string;
}) {
  const [options, setOptions] = useState<{ _id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!value.reimburseTechnician) return;
    let current = true;
    setLoading(true); setLoadError('');
    apiRequest({ method: 'GET', url: 'billable-expenses/technicians', params: { eventId }, isToken: true }).then(({ response }) => {
      if (!current) return;
      if (response?.status === 200 && Array.isArray(response?.data?.data)) setOptions(response.data.data);
      else setLoadError('Unable to load this show’s technicians. Try again.');
    }).catch(() => { if (current) setLoadError('Unable to load technicians. Try again.'); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [eventId, value.reimburseTechnician, attempt]);
  const selected = options.find(o => o._id === value.reimbursementContractorId) ??
    (value.reimbursementContractorId ? { _id: value.reimbursementContractorId, name: 'Previously selected technician' } : null);
  return <Box sx={{ width: '100%', color: '#fff', border: '1px solid #D175B6', borderRadius: 2, p: 2,
    '& p': { fontSize: '0.875rem !important' },
    '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset, &:hover fieldset, &.Mui-focused fieldset': { borderColor: '#D175B6' } },
    '& .MuiInputLabel-root, & .MuiFormHelperText-root': { color: 'rgba(255,255,255,.8)' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#D175B6' },
    '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(255,255,255,.55)' },
    '& .MuiCheckbox-root, & .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': { color: '#D175B6' },
  }}>
    <FormControlLabel control={<Checkbox checked={value.reimburseTechnician} disabled={disabled}
      onChange={(_, checked) => onChange(checked ? { ...value, reimburseTechnician: true, reimbursementAmount: value.reimbursementAmount || clientAmount || '' } : { ...value, reimburseTechnician: false })} />}
      label="Reimburse technician" />
    <Typography variant="body2" sx={{ mb: value.reimburseTechnician ? 2 : 0 }}>
      The client charge and technician reimbursement are separate. This does not record a payment.
    </Typography>
    {value.reimburseTechnician && <Box sx={{ display: 'grid', gap: 2 }}>
      {loadError && <Alert severity="error" action={<Button onClick={() => setAttempt(a => a + 1)}>Retry</Button>}>{loadError}</Alert>}
      {!loading && !loadError && options.length === 0 && <Alert severity="info">Assign a technician to this show before adding a reimbursement.</Alert>}
      <Autocomplete options={options} value={selected} loading={loading} disabled={disabled || loading || !!loadError}
        componentsProps={{ paper: { sx: { bgcolor: '#15102b', color: '#fff', border: '1px solid #D175B6' } } }}
        getOptionLabel={o => o.name} isOptionEqualToValue={(a, b) => a._id === b._id}
        onChange={(_, option) => onChange({ ...value, reimbursementContractorId: option?._id ?? '' })}
        renderInput={params => <TextField {...params} label="Technician on this show" required />} />
      <TextField label="Technician reimbursement amount" type="number" required disabled={disabled}
        value={value.reimbursementAmount} inputProps={{ min: 0.01, step: 0.01 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><span style={{ color: '#fff' }}>$</span></InputAdornment> }}
        onChange={e => onChange({ ...value, reimbursementAmount: e.target.value })}
        helperText="Defaults to the client charge; adjust if the reimbursement differs." />
      <Alert severity="info">Saving approves the reimbursement. Regenerate an issued PO before sending. The client is charged once.</Alert>
    </Box>}
    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
  </Box>;
}
